// ============================================================================
// Tienda online — data access (Supabase).
//
// The public shop only calls the SECURITY DEFINER functions of
// backend/online_store.sql; the account's panel goes through RLS (tenant +
// module flag). Nothing here is trusted by the database.
// ============================================================================
import { supabase, fetchAllRows } from './supabase';
import { getTenant } from './tenant';
import { normaliseSettings, slugify, isValidSlug, cleanDomain } from './onlineStore';

// Raised when the migration has not been applied yet, so the UI can say so.
export class StoreSchemaMissing extends Error {}

const isMissingSchema = (error) => /PGRST205|PGRST202|42P01|42883|does not exist|schema cache/i.test(`${error?.code || ''} ${error?.message || ''}`);
const raise = (error) => {
  if (isMissingSchema(error)) throw new StoreSchemaMissing(error.message);
  throw error;
};

// Error names raised by the shop's functions that the storefront explains.
export const SHOP_ERRORS = [
  'store_not_available', 'orders_paused', 'name_required', 'phone_invalid', 'email_invalid', 'email_taken',
  'password_invalid', 'bad_credentials', 'rate_limited', 'fulfilment_invalid', 'address_required',
  'postcode_not_served', 'payment_invalid', 'items_invalid', 'items_unavailable', 'out_of_stock',
  'below_minimum', 'not_signed_in',
];

function shopError(error) {
  const code = SHOP_ERRORS.find((c) => (error?.message || '').includes(c)) || 'failed';
  const err = new Error(code);
  err.code = code;
  return err;
}

// --- public shop --------------------------------------------------------------

// `where` = { slug, host }: the shop by its /tienda/<slug> or by its own domain.
const at = (where) => ({ p_slug: where.slug || null, p_host: where.host || null });

export async function fetchPublicStore(where) {
  const { data, error } = await supabase.rpc('get_public_store', at(where));
  if (error) throw error;
  return data ? { ...data, settings: normaliseSettings(data.settings) } : null;
}

export async function fetchStoreProducts(where, { category = null, search = '', sort = 'name', page = 1, pageSize = 24, ids = null } = {}) {
  const { data, error } = await supabase.rpc('get_store_products', {
    ...at(where), p_category: category, p_search: search || null, p_sort: sort, p_page: page, p_page_size: pageSize, p_ids: ids,
  });
  if (error) throw error;
  return data || { total: 0, items: [] };
}

export async function placeOrder(where, session, { customer, fulfilment, address, payment, notes, lines }) {
  const { data, error } = await supabase.rpc('store_place_order', {
    ...at(where), p_session: session || null, p_customer: customer, p_fulfilment: fulfilment,
    p_address: fulfilment === 'delivery' ? address : null, p_payment: payment, p_notes: notes || '',
    p_items: lines.map((l) => ({ product_id: l.id, qty: String(l.qty) })),
  });
  if (error) throw shopError(error);
  return data;
}

export async function fetchOrderByToken(token) {
  const { data, error } = await supabase.rpc('get_store_order', { p_token: token });
  if (error) throw error;
  return data;
}

export async function registerCustomer(where, { email, password, name, phone }) {
  const { data, error } = await supabase.rpc('store_customer_register', {
    ...at(where), p_email: email, p_password: password, p_name: name, p_phone: phone || '',
  });
  if (error) throw shopError(error);
  return data;
}

export async function loginCustomer(where, { email, password }) {
  const { data, error } = await supabase.rpc('store_customer_login', { ...at(where), p_email: email, p_password: password });
  if (error) throw shopError(error);
  if (data?.error) throw shopError({ message: data.error });
  return data;
}

export async function logoutCustomer(session) {
  await supabase.rpc('store_customer_logout', { p_session: session });
}

/** { customer, orders } or null when the session is gone. */
export async function fetchCustomerAccount(where, session) {
  if (!session) return null;
  const { data, error } = await supabase.rpc('store_customer_account', { ...at(where), p_session: session });
  if (error) throw error;
  return data;
}

export async function updateCustomer(where, session, patch) {
  const { data, error } = await supabase.rpc('store_customer_update', { ...at(where), p_session: session, p_data: patch });
  if (error) throw shopError(error);
  return data;
}

// --- the account's panel --------------------------------------------------------

/** The account's shop row, or null when it has not set one up yet. */
export async function getMyStore() {
  const { ownerId } = await getTenant();
  const { data, error } = await supabase.from('online_stores').select('*').eq('user_id', ownerId).maybeSingle();
  if (error) raise(error);
  return data ? { ...data, settings: normaliseSettings(data.settings) } : null;
}

/** A free slug from the business name ("super-fresco", "super-fresco-2", …). */
async function freeSlug(name) {
  const base = isValidSlug(slugify(name)) ? slugify(name) : 'tienda';
  for (let i = 0; i < 6; i += 1) {
    const candidate = i === 0 ? base : `${base.slice(0, 52)}-${Math.floor(Math.random() * 9000 + 1000)}`;
    // eslint-disable-next-line no-await-in-loop
    const { data } = await supabase.rpc('get_public_store', { p_slug: candidate, p_host: null });
    if (!data) return candidate;   // a draft of another account also answers null: the insert below settles it
  }
  return `tienda-${Date.now().toString(36)}`;
}

/** First visit: a draft shop named after the business. */
export async function createMyStore() {
  const { ownerId } = await getTenant();
  const { data: prof } = await supabase.from('profiles').select('company_name, phone, address, logo_url').eq('id', ownerId).single();
  const settings = normaliseSettings({
    name: prof?.company_name || '', phone: prof?.phone || '', address: prof?.address || '', logo_url: prof?.logo_url || '',
  });
  for (let attempt = 0; attempt < 3; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const slug = await freeSlug(prof?.company_name);
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await supabase.from('online_stores')
      .insert({ user_id: ownerId, slug, settings }).select('*').single();
    if (!error) return { ...data, settings: normaliseSettings(data.settings) };
    if (error.code !== '23505' || !/slug/.test(error.message || '')) raise(error);
  }
  throw new Error('slug_taken');
}

/** Save address, own domain, published state and settings. */
export async function saveMyStore(id, { slug, custom_domain, status, settings }) {
  const row = { settings: normaliseSettings(settings) };
  if (slug !== undefined) {
    if (!isValidSlug(slug)) throw Object.assign(new Error('slug_invalid'), { code: 'slug_invalid' });
    row.slug = slug;
  }
  if (custom_domain !== undefined) {
    const domain = cleanDomain(custom_domain);
    if (custom_domain && !domain) throw Object.assign(new Error('domain_invalid'), { code: 'domain_invalid' });
    row.custom_domain = domain || null;
  }
  if (status !== undefined) row.status = status;
  const { data, error } = await supabase.from('online_stores').update(row).eq('id', id).select('*');
  if (error) {
    if (error.code === '23505') {
      const code = /custom_domain/.test(error.message || '') ? 'domain_taken' : 'slug_taken';
      throw Object.assign(new Error(code), { code });
    }
    raise(error);
  }
  // RLS filters silently: no row back = not allowed.
  if (!data || data.length === 0) throw Object.assign(new Error('forbidden'), { code: 'forbidden' });
  return { ...data[0], settings: normaliseSettings(data[0].settings) };
}

const ORDER_COLUMNS = 'id, order_number, token, customer_id, customer_name, customer_email, customer_phone, customer_nif, fulfilment, address, notes, items, subtotal, delivery_fee, total, payment_method, payment_status, payment_ref, paid_at, status, invoice_id, created_at, updated_at';

/** Orders from `from` (ISO) on, newest first, paged past the 1000-row cap. */
export async function listOrders({ from = null } = {}) {
  const { ownerId } = await getTenant();
  try {
    return await fetchAllRows((lo, hi) => {
      let query = supabase.from('online_store_orders').select(ORDER_COLUMNS).eq('user_id', ownerId);
      if (from) query = query.gte('created_at', from);
      return query.order('created_at', { ascending: false }).order('id', { ascending: true }).range(lo, hi);
    });
  } catch (e) {
    return raise(e);
  }
}

export async function updateOrder(id, patch) {
  const { data, error } = await supabase.from('online_store_orders').update(patch).eq('id', id).select(ORDER_COLUMNS);
  if (error) raise(error);
  if (!data || data.length === 0) throw new Error('forbidden');
  return data[0];
}

/** Orders still waiting for the shop to look at them (nav / Productos badge). */
export async function countNewOrders() {
  try {
    const { ownerId } = await getTenant();
    const { count, error } = await supabase.from('online_store_orders')
      .select('id', { count: 'exact', head: true }).eq('user_id', ownerId).eq('status', 'new');
    return error ? 0 : (count || 0);
  } catch {
    return 0;
  }
}

export async function listCustomers() {
  const { ownerId } = await getTenant();
  const { data, error } = await supabase.from('online_store_customers')
    .select('id, email, name, phone, nif, address, created_at, last_login_at')
    .eq('user_id', ownerId).order('created_at', { ascending: false }).limit(1000);
  if (error) raise(error);
  return data || [];
}

export async function resetCustomerPassword(customerId, password) {
  const { error } = await supabase.rpc('store_customer_set_password', { p_customer: customerId, p_password: password });
  if (error) throw shopError(error);
}

/** How many of the account's articles are in / out of the shop. */
export async function productVisibilityCounts() {
  const { ownerId } = await getTenant();
  const count = async (filter) => {
    const { count: n, error } = await filter(supabase.from('products').select('id', { count: 'exact', head: true }).eq('user_id', ownerId));
    if (error) raise(error);
    return n || 0;
  };
  const [total, hidden, noPhoto] = await Promise.all([
    count((q) => q),
    count((q) => q.eq('show_in_store', false)),
    count((q) => q.eq('show_in_store', true).is('image_url', null)),
  ]);
  return { total, hidden, noPhoto };
}

/** Every article of the account with what the shop tools need (paged past the 1000-row cap). */
export async function listStoreArticles() {
  const { ownerId } = await getTenant();
  try {
    return await fetchAllRows((lo, hi) => supabase.from('products')
      .select('id, name, barcode, category, family, web_category, image_url, show_in_store, unit_price')
      .eq('user_id', ownerId).order('id', { ascending: true }).range(lo, hi));
  } catch (e) {
    return raise(e);
  }
}

/** Write web categories ([{id, category}]) in batches; returns how many changed. */
export async function saveWebCategories(changes, onProgress) {
  let done = 0;
  for (let i = 0; i < changes.length; i += 1000) {
    const slice = changes.slice(i, i + 1000);
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await supabase.rpc('store_set_web_categories', { p_items: slice });
    if (error) raise(error);
    done += data || 0;
    if (onProgress) onProgress(Math.min(changes.length, i + slice.length), changes.length);
  }
  return done;
}

/** Fill empty photos with Open Food Facts images ([{id, url}]); returns how many changed. */
export async function saveProductPhotos(items) {
  if (!items.length) return 0;
  const { data, error } = await supabase.rpc('store_set_product_photos', { p_items: items });
  if (error) raise(error);
  return data || 0;
}

export async function setProductInStore(productId, show) {
  const { data, error } = await supabase.from('products').update({ show_in_store: !!show }).eq('id', productId).select('id');
  if (error) raise(error);
  if (!data || data.length === 0) throw new Error('forbidden');
}

/** Upload a logo / banner to the account's folder in the public 'logos' bucket. */
export async function uploadStoreImage(file, kind) {
  if (!file || !/^image\/(png|jpe?g|webp)$/i.test(file.type)) throw new Error('invalid_image');
  if (file.size > 5 * 1024 * 1024) throw new Error('image_too_large');
  const { user } = await getTenant();
  const ext = (file.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
  const path = `${user.id}/store_${kind}_${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('logos').upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  return supabase.storage.from('logos').getPublicUrl(path).data.publicUrl;
}
